import _ from 'lodash'
import classNames from 'classnames'
import dayjs from 'dayjs'
import React, {useMemo} from 'react'
import {Link} from 'react-router-dom'
import {GoDotFill} from 'react-icons/go'
import {IoCalendarOutline, IoChevronDown} from 'react-icons/io5'
import {BsClock} from 'react-icons/bs'
import {PiWarningCircleFill} from 'react-icons/pi'
import useTranslate from 'modules/local/useTranslate'
import {formatSimplifyDate} from 'helpers/dateUtils'
import getLinkToDetail from '../../../../helpers/getLinkToDetail'
import {CourseStates} from '../WorkspaceCourse'

// Tiến độ theo TUẦN của khóa - ƯỚC TÍNH từ time_open/time_close (giả định lịch học đều đặn hàng
// tuần, không có tuần nghỉ) - KHÔNG phải đếm thật số buổi học đã dạy (chưa có dữ liệu đó ở cấp khóa,
// chỉ có ở từng buổi/timetable_detail của từng lớp). Tách từ CourseClassOperations.js (nơi khai
// sinh) sang đây - giờ CourseHeroHeader (dùng chung ở cả CourseDetail.js lẫn CourseClassOperations.js,
// xem comment component bên dưới) mới là nơi TÍNH progress, 2 nơi gọi chỉ còn truyền currentCourse.
export const getWeekProgress = (time_open, time_close) => {
    if (!time_open || !time_close) return null
    const start = dayjs(time_open)
    const end = dayjs(time_close)
    const totalDays = end.diff(start, 'day')
    if (totalDays <= 0) return null
    const totalWeeks = Math.max(1, Math.ceil(totalDays / 7))
    const elapsedDays = Math.min(Math.max(dayjs().diff(start, 'day'), 0), totalDays)
    const currentWeek = Math.min(totalWeeks, Math.max(0, Math.ceil(elapsedDays / 7)))
    const percent = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100)))
    return {totalWeeks, currentWeek, percent}
}

// InfoBlock/EmptyNote - tách từ CourseClassOperations.js (nơi khai sinh) sang đây CÙNG LÝ DO với
// getWeekProgress ở trên: CourseDetail.js giờ được CourseClassOperations.js import ngược lại (nhúng
// thẳng <CourseDetail/> làm tab Tổng quan, xem "dùng lại, không clone" bạn yêu cầu) - nếu 2 khối này
// còn nằm ở CourseClassOperations.js sẽ tạo import vòng (CourseClassOperations -> CourseDetail ->
// CourseClassOperations). Đặt ở đây (file trung lập, không import ngược 2 file kia) để cắt vòng lặp.

// Card wrapper dùng chung cho các khối thông tin khóa - tiêu đề uppercase nhỏ + icon, đúng phong
// cách card đang dùng trong CourseDashboard.js (title uppercase, border/shadow nhẹ).
export const InfoBlock = ({icon, title, action, children}) => (
    <div className="p-4 flex flex-col gap-3 background border border-color-50 shadow-md rounded-xl">
        <div className="flex items-center justify-between gap-2">
            <h4 className="flex items-center gap-2 font-bold text-sm text-slate-600 uppercase tracking-wider">
                {icon}
                {title}
            </h4>
            {action}
        </div>
        {children}
    </div>
)

// Bạn yêu cầu: phần nào chưa có dữ liệu phải hiện rõ empty-placeholder (không ẩn/lặng im) để admin
// biết mà bổ sung - dùng chung cho cả field đơn lẻ lẫn cả khối rỗng (list rỗng).
export const EmptyNote = ({children}) => (
    <span className="inline-flex items-center gap-1 text-xs text-amber-600 italic tracking-wide">
        <PiWarningCircleFill size={13} className="flex-shrink-0"/>
        {children}
    </span>
)

// Header "hero" nền navy dùng CHUNG cho trang chi tiết Khóa học (CourseDetail.js, tab Tổng quan) và
// màn vận hành (CourseClassOperations.js, khối "Quản trị Khóa học") - 2 nơi TRƯỚC ĐÂY tự vẽ 2 bản
// gần giống hệt nhau (badge trạng thái/năm học, khung ngày mở-đóng, tên khóa + số liệu, mã khóa/hạn
// ghi danh, thanh tiến trình) - bạn yêu cầu gộp lại đúng 1 thiết kế (lấy nguyên bản CourseDetail.js),
// khác nhau qua PROP thay vì lặp code 2 nơi.
//
// stats: mảng ĐÚNG 3 phần tử {value, label} - label truyền vào ĐÃ dịch sẵn (caller tự t(), component
// không đoán key). CourseDetail.js truyền Lớp/Học viên/Giảng viên; CourseClassOperations.js truyền
// Lớp/Học viên/Thời lượng(tuần) - mỗi ngữ cảnh tự quyết định 3 số liệu quan trọng nhất, không ép cứng.
// eyebrow: nhãn nhỏ chữ hoa phía trên tên khóa (vd "course administration") - optional, truyền i18n
// key (component tự t()).
// actions: node tuỳ chọn (vd nút "Cập nhật Thông tin Khóa") - đứng cạnh khung ngày, TRƯỚC chevron;
// action tự lo onClick + stopPropagation (không auto) vì header có thể đang lắng nghe click để toggle
// collapse (xem collapsible bên dưới).
// collapsible/collapsed/onToggleCollapse: bấm cả header để thu gọn/mở khối nội dung ĐỨNG NGOÀI
// component này - component KHÔNG tự giữ state/tự quyết định ẩn hiện gì bên dưới nó, chỉ vẽ chevron
// đúng trạng thái + phát sự kiện qua onToggleCollapse.
// titleOpensNewTab: true khi header nhúng ở 1 trang KHÁC trang chi tiết (CourseClassOperations.js -
// bấm tên khóa mở tab mới, không rời trang đang thao tác); false khi header CHÍNH LÀ trang chi tiết
// (CourseDetail.js - đã đứng sẵn ở đó).
// className: override khung NGOÀI CÙNG (margin/bo góc/shadow) - CourseDetail.js cần tự bo góc+shadow+
// margin (đứng riêng 1 khối); CourseClassOperations.js truyền rỗng/khác (header chỉ là phần TRÊN của
// 1 card lớn hơn đã tự bo góc+shadow+overflow-hidden ở component cha, nối liền xuống khung amber bên
// dưới - xem CourseClassOperations.js).
export const CourseHeroHeader = ({
    currentCourse,
    stats,
    eyebrow,
    actions,
    collapsible = false,
    collapsed = false,
    onToggleCollapse,
    titleOpensNewTab = false,
    className = 'mb-6 rounded-2xl shadow-lg',
}) => {
    const t = useTranslate()
    const courseStateInfo = _.find(CourseStates, {value: currentCourse?.course_state})
    const weekProgress = useMemo(
        () => getWeekProgress(currentCourse?.time_open, currentCourse?.time_close),
        [currentCourse?.time_open, currentCourse?.time_close]
    )

    return (
        <div
            onClick={collapsible ? onToggleCollapse : undefined}
            // Hex literal (KHÔNG var(--color-education-900)) - component này dùng CHUNG ở nhiều trang,
            // không phải trang nào cũng bọc class .education-theme (CourseDetail.js có, nhưng
            // CourseClassOperations.js thì KHÔNG) - biến CSS không resolve được sẽ làm nền trong suốt,
            // chữ trắng gần như vô hình (đúng lỗi bạn báo, khớp bài học đã ghi ở CoursePortalTopNav.js
            // cho chính lý do này). #1c4a75 = giá trị thật của --color-education-900 trong custom.scss.
            style={{background: 'linear-gradient(90deg, #1c4a75 0%, #0a2d4a 100%)'}}
            className={classNames('p-4 md:p-5 flex flex-col gap-3', collapsible && 'cursor-pointer', className)}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center flex-wrap gap-2">
                    {!!courseStateInfo && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 bg-white bg-opacity-10 text-white">
                            <GoDotFill size={10} className={currentCourse?.course_state === 'completed' ? 'text-color-300' : 'text-green-400'}/>
                            {t(courseStateInfo.label)}
                        </span>
                    )}
                    {!!currentCourse?.academic_year && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-white bg-opacity-10 text-white text-opacity-80">
                            {`${t('academic year')} ${currentCourse.academic_year}`}
                        </span>
                    )}
                </div>
                <div className="flex items-center flex-wrap gap-2 flex-shrink-0">
                    {(currentCourse?.time_open || currentCourse?.time_close) && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white bg-opacity-10 text-xs text-white text-opacity-80 whitespace-no-wrap">
                            <IoCalendarOutline size={13}/>
                            {`${t('start')}: `}<span className="font-semibold text-white">{formatSimplifyDate(currentCourse.time_open, null, 'DD/MM/YYYY')}</span>
                            <span className="text-white text-opacity-40">·</span>
                            {`${t('end')}: `}<span className="font-semibold text-white">{formatSimplifyDate(currentCourse.time_close, null, 'DD/MM/YYYY')}</span>
                        </div>
                    )}
                    {actions}
                    {collapsible && (
                        <span
                            className="flex flex-center flex-shrink-0 rounded-full border"
                            style={{width: 32, height: 32, borderColor: 'rgba(252,211,77,0.45)', background: 'rgba(255,255,255,0.08)'}}>
                            <IoChevronDown
                                size={18}
                                style={{color: '#fcd34d'}}
                                className={classNames('transition-transform duration-200', !collapsed && 'rotate-180')}
                            />
                        </span>
                    )}
                </div>
            </div>

            {!!eyebrow && (
                <span className="font-semibold text-xs uppercase tracking-widest" style={{color: '#fcd34d'}}>
                    {t(eyebrow)}
                </span>
            )}

            <div className="flex flex-wrap items-start justify-between gap-4">
                <Link
                    to={getLinkToDetail(currentCourse)}
                    target={titleOpensNewTab ? '_blank' : undefined}
                    className="no-underline min-w-0"
                    onClick={(e) => collapsible && e.stopPropagation()}>
                    <h1 className="text-xl md:text-2xl font-bold text-white leading-snug m-0 truncate">
                        <span className="font-semibold text-white text-opacity-60">{t("course")}: </span>
                        {currentCourse?.title}
                    </h1>
                </Link>
                {!!stats?.length && (
                    <div className="flex items-center gap-4 flex-shrink-0">
                        {stats.map((s, i) => (
                            <div key={i} className="flex flex-col items-center">
                                <span className="font-bold text-lg text-white">{s.value}</span>
                                <span className="text-2xs uppercase tracking-wider whitespace-no-wrap text-white text-opacity-60">{s.label}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="flex items-center flex-wrap gap-2 text-xs">
                {currentCourse?.course_idcode && (
                    <span className="px-2 py-0.5 rounded-md bg-white bg-opacity-10 font-mono font-semibold text-white text-opacity-90">
                        {`${t('course code')}: `}{currentCourse.course_idcode}
                    </span>
                )}
                {/* Hạn ghi danh cuối - LUÔN hiện kèm fallback "—" (không ẩn hẳn khi trống) để admin
                    biết ngay là còn thiếu khai báo, thay vì tưởng field không tồn tại. */}
                <span className="text-white text-opacity-60">
                    {`${t('deadline')}: `}
                    <span className="font-semibold text-amber-300">
                        {currentCourse?.last_enrollment_time
                            ? formatSimplifyDate(currentCourse.last_enrollment_time, null, 'DD/MM/YYYY')
                            : '—'}
                    </span>
                </span>
            </div>

            {!!weekProgress && (
                <div className="flex flex-wrap items-center gap-3 pt-1">
                    <span className="flex items-center gap-1.5 text-xs flex-shrink-0 text-white text-opacity-60">
                        <BsClock size={12}/>
                        {t('teaching progress by schedule')}
                    </span>
                    <div className="flex-1 min-w-24 h-2 rounded-full overflow-hidden bg-white bg-opacity-15">
                        <div
                            style={{width: `${weekProgress.percent}%`, background: '#fcd34d'}}
                            className="h-full rounded-full transition-all duration-500"
                        />
                    </div>
                    <span className="font-semibold text-xs whitespace-no-wrap flex-shrink-0 text-amber-300">
                        {`${t('weeks elapsed prefix')} ${weekProgress.currentWeek}/${weekProgress.totalWeeks} ${t('week')} (${weekProgress.percent}%)`}
                    </span>
                </div>
            )}
        </div>
    )
}

export default CourseHeroHeader
